import * as THREE from 'three';
import type { BetZone } from '@sdl/shared';
import { BettingZone } from './BettingZone';
import { Bill } from './Bill';
import { DropZoneOwnerLabel } from './DropZoneOwnerLabel';
import { CreateZoneLockOverlay } from './CreateZoneLockOverlay';
import type { PhysicsWorld } from '../../engine/physics/PhysicsWorld';

type ZoneKind = 'banker' | 'sidebet' | 'create';

interface BetZoneVisual {
  id: string;
  kind: ZoneKind;
  zone: BettingZone;
  label: DropZoneOwnerLabel | null;
  fadedPlayersPanel?: null;
  data?: BetZone;
  /** Lock overlay shown on banker / shooter zones while ownerAmount === 0
   *  AND the local viewer is not the owner — signals "waiting for the
   *  banker (cee-lo) / shooter (craps) to place their bet". Hides as
   *  soon as the owner places (ownerAmount > 0). Reuses the same lock
   *  overlay class as the sidebet-cap lock on the create zone. */
  lockOverlay?: CreateZoneLockOverlay | null;
}

const DEFAULT_CENTER = { x: 0, y: 0.01, z: 0 };
/** Banker bet zone, viewed by a fader, while the banker still hasn't set the pot. */
const BANKER_PENDING_YELLOW = 0xFFD700;
/** Banker bet zone, viewed by a fader, once the banker has placed their pot → "fade ready". */
const BANKER_READY_GREEN = 0x7CFF7A;
/** Any zone owned by the local player (banker-is-me, or my sidebet). */
const LOCAL_OWNED_BLUE = 0x4FC3F7;
/** Opponent-owned sidebet zone. Same green as banker-ready; means "actionable / fade-able". */
const OPPONENT_OWNED_GREEN = 0x7CFF7A;
const CREATE_COLOR = 0x43D9D9;
const DEFAULT_RADIUS = 1.1;
const ZONE_MARGIN = 0.3; // Margin between zones
const HEX_SPACING = DEFAULT_RADIUS * 2 + ZONE_MARGIN;
const LABEL_Z_OFFSET = DEFAULT_RADIUS + 0.15;

/** Pooled zone visual (detached, ready for reuse) */
interface PooledZoneVisual {
  zone: BettingZone;
  label: DropZoneOwnerLabel;
  fadedPlayersPanel: null;
}

export class BetZoneManager {
  private scene: THREE.Scene;
  private zones = new Map<string, BetZoneVisual>();
  private createZone: BetZoneVisual;
  private center = new THREE.Vector3(DEFAULT_CENTER.x, DEFAULT_CENTER.y, DEFAULT_CENTER.z);
  private createZoneVisible = false;
  /** When true, drops on the create zone are blocked and the lock overlay
   * (lock icon + PLEASE WAIT) is shown. Set by GameFlowController based on
   * the visible-sidebet cap. */
  private createZoneLocked = false;
  private createZoneLockOverlay: CreateZoneLockOverlay | null = null;
  private currentHover: string | null = null;
  /** Used to decide zone ring colors (blue = mine, green = opponent's). */
  private localPlayerId: string | null = null;

  /** Pool of detached zone visuals for reuse */
  private zonePool: PooledZoneVisual[] = [];

  /** Stable hex slot index per sidebet zone (zoneId → slot) */
  private zoneSlots = new Map<string, number>();

  /** playerId → avatar picture URL, refreshed each syncZones. Read in
   * upsertZone so the 3D owner label shows the real profile picture. */
  private playerAvatars = new Map<string, string>();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    const createZoneMesh = new BettingZone({
      scene: this.scene,
      position: DEFAULT_CENTER,
      radius: DEFAULT_RADIUS,
      color: CREATE_COLOR,
      glowColor: CREATE_COLOR,
    });

    this.createZone = {
      id: 'local-create',
      kind: 'create',
      zone: createZoneMesh,
      // No 3D "PLACE BET / CHOOSE TYPE" label — the new HTML
      // InstructionBanner ("Place a sidebet" + icon legend) replaces it. The
      // create zone is now a pure drop target with no in-world copy.
      label: null,
    };
    // Lock overlay: parented to the create zone's group so it tracks position
    // automatically, hidden by default, surfaced via setCreateZoneLocked().
    // Position is set inside the overlay (centered in the zone, slightly
    // above ground).
    this.createZoneLockOverlay = new CreateZoneLockOverlay(createZoneMesh.getGroup());
  }

  setCenter(position: { x: number; y: number; z: number }): void {
    this.center.set(position.x, position.y, position.z);
  }

  getCenter(): { x: number; y: number; z: number } {
    return { x: this.center.x, y: this.center.y, z: this.center.z };
  }

  setLocalPlayerId(playerId: string | null): void {
    this.localPlayerId = playerId;
  }

  /**
   * Ring color policy:
   *   - Banker zone → blue if viewer IS the banker, yellow until the banker
   *     sets the pot, green after (fade-ready for the viewer).
   *   - Sidebet zone → blue if the viewer owns (created) it, green otherwise.
   */
  private resolveZoneColor(zone: BetZone): number {
    const ownedByMe = !!this.localPlayerId && zone.ownerPlayerId === this.localPlayerId;
    if (zone.type === 'sidebet') {
      return ownedByMe ? LOCAL_OWNED_BLUE : OPPONENT_OWNED_GREEN;
    }
    // banker zone
    if (ownedByMe) return LOCAL_OWNED_BLUE;
    return zone.ownerAmount > 0 ? BANKER_READY_GREEN : BANKER_PENDING_YELLOW;
  }

  setCreateZonePosition(position: { x: number; y: number; z: number }): void {
    this.createZone.zone.setPosition(position);
    if (this.createZone.label) {
      this.createZone.label.setPosition(0, 0.7, -LABEL_Z_OFFSET);
    }
  }

  getCreateZonePosition(): { x: number; y: number; z: number } {
    const pos = this.createZone.zone.getPosition();
    return { x: pos.x, y: pos.y, z: pos.z };
  }

  setCreateZoneVisible(visible: boolean): void {
    this.createZoneVisible = visible;
    if (visible) {
      this.createZone.zone.show();
      if (this.createZone.label) this.createZone.label.setVisible(true);
    } else {
      this.createZone.zone.hide();
      if (this.createZone.label) this.createZone.label.setVisible(false);
      this.createZone.zone.deactivateGlow();
      if (this.currentHover === 'create') {
        this.currentHover = null;
      }
      // Hide lock overlay when zone itself is hidden (phase exit etc.)
      this.createZoneLockOverlay?.setVisible(false);
    }
  }

  /**
   * Toggle the create zone's lock state. Locked = visible zone with lock
   * icon + PLEASE WAIT overlay, drops blocked. The zone stays in the world
   * (so the player keeps spatial context) but won't accept bills until the
   * cap clears.
   */
  setCreateZoneLocked(locked: boolean): void {
    this.createZoneLocked = locked;
    // Only show the overlay if the zone itself is visible (PLAYER_BETTING /
    // PLAYER_ROLL phase). Outside those phases the zone is hidden, so the
    // overlay stays hidden too.
    const showOverlay = locked && this.createZoneVisible;
    this.createZoneLockOverlay?.setVisible(showOverlay);
    if (locked) {
      this.createZone.zone.deactivateGlow();
      if (this.currentHover === 'create') {
        this.currentHover = null;
      }
    }
  }

  isCreateZoneLocked(): boolean {
    return this.createZoneLocked;
  }

  syncZones(zones: BetZone[], playerNames: Map<string, string>, playerAvatars?: Map<string, string>): void {
    this.playerAvatars = playerAvatars ?? new Map();
    const existingIds = new Set(this.zones.keys());
    const bankerZone = zones.find((zone) => zone.type === 'banker') ?? null;
    const sideZones = zones.filter((zone) => zone.type === 'sidebet');
    const orderedSideZones = [...sideZones].sort((a, b) => a.createdAt - b.createdAt);

    // Create/update banker zone at dynamic center (only on first creation)
    if (bankerZone) {
      this.upsertZone(bankerZone, playerNames, this.center, this.resolveZoneColor(bankerZone));
      existingIds.delete(bankerZone.id);
    }

    // Get the banker zone's actual position as origin for side zones
    const existingBankerZone = bankerZone ? this.zones.get(bankerZone.id) : null;
    const bankerPosition = existingBankerZone?.zone.getPosition() ?? this.center;
    const bankerLocked = bankerZone?.status === 'locked';

    // Separate open vs locked sidebet zones.
    // Locked zones keep their assigned slot but free it for reuse by new zones.
    // Queued zones (visible: false) participate in NEITHER slot assignment nor
    // visual creation — they're invisible until the server flips visible:true.
    // Including them in slot assignment would consume slot indices and produce
    // visual gaps when slots free up.
    const openSideZones = orderedSideZones.filter((z) => z.status !== 'locked' && z.visible !== false);
    const lockedSideZones = orderedSideZones.filter((z) => z.status === 'locked');

    // Remove stale slot assignments for zones no longer in the server list,
    // OR for zones that are queued (slots are only for visible zones).
    const activeVisibleSideIds = new Set(
      orderedSideZones.filter((z) => z.visible !== false).map((z) => z.id)
    );
    for (const [id] of this.zoneSlots) {
      if (!activeVisibleSideIds.has(id)) this.zoneSlots.delete(id);
    }

    // Locked zones keep their position but don't block the slot for new zones
    const lockedSlots = new Set<number>();
    for (const zone of lockedSideZones) {
      const slot = this.zoneSlots.get(zone.id);
      if (slot !== undefined) lockedSlots.add(slot);
    }

    // Build set of slots occupied by OPEN VISIBLE zones only (these block new assignments)
    const usedSlots = new Set<number>();
    for (const zone of openSideZones) {
      const slot = this.zoneSlots.get(zone.id);
      if (slot !== undefined) usedSlots.add(slot);
    }

    // Assign slots to new open VISIBLE zones, reusing slots freed by locked/removed zones
    for (const zone of openSideZones) {
      if (!this.zoneSlots.has(zone.id)) {
        let slot = 0;
        while (usedSlots.has(slot)) slot++;
        this.zoneSlots.set(zone.id, slot);
        usedSlots.add(slot);
      }
    }

    // Compute hex positions for sidebets.
    // When banker zone is locked (hidden), the center position (slot 0) is the
    // banker's old spot. Hex ring positions start at slot 1.
    // When banker is visible, slot 0 is the first hex ring position (adjacent to banker).
    const allSlotValues = [...this.zoneSlots.values()];
    const maxSlot = allSlotValues.length > 0 ? Math.max(...allSlotValues) + 1 : 0;
    const hexPositions = this.getHexPositions(
      bankerLocked ? Math.max(maxSlot - 1, 0) : maxSlot,
      HEX_SPACING,
      bankerPosition
    );

    // Build final positions array: if banker locked, center is slot 0, hex ring starts at slot 1
    const sidePositions: THREE.Vector3[] = bankerLocked
      ? [bankerPosition.clone(), ...hexPositions]
      : hexPositions;

    for (const zone of orderedSideZones) {
      // Queued zones never enter the visual map — pass them through upsertZone
      // (which early-returns on visible: false to keep any pre-existing
      // visual hidden), but do NOT call existingIds.delete so that, if a
      // visible zone was somehow flipped queued, removeZone() runs.
      if (zone.visible === false) {
        this.upsertZone(zone, playerNames, bankerPosition, this.resolveZoneColor(zone));
        // The zone is invisible — leave existingIds entry (if any) so removeZone
        // cleans up an orphaned visual.
        continue;
      }
      const slot = this.zoneSlots.get(zone.id) ?? 0;
      const pos = sidePositions[slot] ?? bankerPosition;
      this.upsertZone(zone, playerNames, pos, this.resolveZoneColor(zone));
      existingIds.delete(zone.id);
    }

    for (const zoneId of existingIds) {
      this.removeZone(zoneId);
    }
  }

  update(deltaTime: number): void {
    for (const zone of this.zones.values()) {
      zone.zone.update(deltaTime);
    }
    this.createZone.zone.update(deltaTime);
  }

  getDropTarget(point: { x: number; z: number }): { kind: ZoneKind; zoneId?: string } | null {
    for (const zone of this.zones.values()) {
      // Skip locked zones — they're hidden but still in the map until the server
      // removes them. Without this check, a locked zone at the same hex slot as a
      // new open zone would be hit first (Map insertion order) causing the drop to
      // target the wrong (locked) zone and be silently rejected by the server.
      if (zone.data?.status === 'locked') continue;
      if (zone.zone.isPointInZone(point)) {
        return { kind: zone.kind, zoneId: zone.id };
      }
    }

    // Locked create zone: not a valid drop target — server would reject.
    if (!this.createZoneLocked && this.createZone.zone.isPointInZone(point)) {
      return { kind: 'create' };
    }

    return null;
  }

  /**
   * Count of visible (non-locked) sidebet zones in the top hex row. The top
   * row is the upper-back ring positions (Z significantly less than the
   * betting center on the camera-back axis) — visually the two zones the
   * player sees framed against the horizon. Used by PlayerBettingState to
   * gate the instruction banner's subtitle: once both top slots are taken,
   * collapse the subtitle so it stops covering the action.
   */
  countTopRowSidebets(): number {
    // 0.866 * HEX_SPACING is the exact Z offset of ring-1 upper slots; pick
    // a threshold a bit short of that so we still match if a position drifts
    // (rounding, alt layouts).
    const zThreshold = -HEX_SPACING * 0.5;
    let count = 0;
    for (const zone of this.zones.values()) {
      if (zone.kind !== 'sidebet') continue;
      const data = zone.data;
      if (!data || data.visible === false) continue;
      if (data.status === 'locked') continue;
      const pos = zone.zone.getPosition();
      if (pos.z - this.center.z < zThreshold) count += 1;
    }
    return count;
  }

  /**
   * Whether the visible sidebet count has reached the point where ring 1's
   * outer LEFT slot (and beyond) is filled — i.e. ≥ 4 zones with the banker
   * visible. Triggers the camera dolly. Count-based rather than position-
   * based since fill order is sequential (slot 0,1,2,3,…) and the spatial
   * "wing" definition shifts when the banker zone hides.
   */
  hasWingSidebets(): boolean {
    let count = 0;
    for (const zone of this.zones.values()) {
      if (zone.kind !== 'sidebet') continue;
      const data = zone.data;
      if (!data || data.visible === false) continue;
      if (data.status === 'locked') continue;
      count += 1;
      if (count >= 4) return true;
    }
    return false;
  }

  getZoneBounds(): { minX: number; maxX: number; minZ: number; maxZ: number } | null {
    const entries: Array<{ pos: THREE.Vector3; radius: number }> = [];
    for (const zone of this.zones.values()) {
      // Skip queued sidebets (server marks them visible:false until a slot
      // frees up). Including them in bounds would make the camera widen
      // for zones nobody can see.
      if (zone.data?.visible === false) continue;
      entries.push({ pos: zone.zone.getPosition(), radius: zone.zone.getRadius() });
    }
    if (this.createZone.zone) {
      entries.push({ pos: this.createZone.zone.getPosition(), radius: this.createZone.zone.getRadius() });
    }
    if (entries.length === 0) return null;

    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const { pos, radius } of entries) {
      minX = Math.min(minX, pos.x - radius);
      maxX = Math.max(maxX, pos.x + radius);
      minZ = Math.min(minZ, pos.z - radius);
      maxZ = Math.max(maxZ, pos.z + radius);
    }

    return { minX, maxX, minZ, maxZ };
  }

  getPlacedBills(): Bill[] {
    const bills: Bill[] = [];
    for (const zone of this.zones.values()) {
      bills.push(...zone.zone.getPlacedBills());
    }
    bills.push(...this.createZone.zone.getPlacedBills());
    return bills;
  }

  clearZoneBills(zoneId: string): Bill[] {
    const zone = this.zones.get(zoneId);
    if (!zone) return [];
    return zone.zone.clearBills();
  }

  /** Bills currently placed in a single zone (used to diff against server notes). */
  getZonePlacedBills(zoneId: string): Bill[] {
    const zone = this.zones.get(zoneId);
    if (!zone) return [];
    return zone.zone.getPlacedBills();
  }

  getZonePositionById(zoneId: string): { x: number; y: number; z: number } | null {
    const zone = this.zones.get(zoneId);
    if (!zone) return null;
    const pos = zone.zone.getPosition();
    return { x: pos.x, y: pos.y, z: pos.z };
  }

  placeBillsInZone(zoneId: string, bills: Bill[], physics: PhysicsWorld, onBillLanded?: () => void, muteDropSound = false): void {
    const zone = this.zones.get(zoneId);
    if (!zone) return;
    zone.zone.dropBillsWithPhysics(bills, physics, () => {}, onBillLanded, muteDropSound);
  }

  placeBillsInZoneImmediate(zoneId: string, bills: Bill[]): void {
    const zone = this.zones.get(zoneId);
    if (!zone) return;
    zone.zone.placeBillsImmediate(bills);
  }

  placeBillsInCreateZone(bills: Bill[], physics: PhysicsWorld, onBillLanded?: () => void): void {
    this.createZone.zone.dropBillsWithPhysics(bills, physics, () => {}, onBillLanded);
  }

  clearCreateZoneBills(): Bill[] {
    return this.createZone.zone.clearBills();
  }

  removeBillsFromZone(zoneId: string, bills: Bill[]): void {
    const zone = this.zones.get(zoneId);
    if (!zone) return;
    for (const bill of bills) {
      zone.zone.removeBill(bill);
    }
  }

  hideZone(zoneId: string): void {
    const zone = this.zones.get(zoneId);
    if (!zone) return;
    zone.zone.hide();
    if (zone.label) zone.label.setVisible(false);
  }

  hideAll(): void {
    for (const zoneId of this.zones.keys()) {
      this.hideZone(zoneId);
    }
    this.setCreateZoneVisible(false);
  }

  /**
   * Reset all zones for phase transition (hide + reset glow/scale state).
   * Preserves pooled zones but ensures clean visual state.
   * Call this when transitioning from betting to shooting phase.
   */
  resetAllZonesForPhaseTransition(): void {
    // Reset and hide all active zones
    for (const zone of this.zones.values()) {
      zone.zone.deactivateGlow();
      zone.zone.hide();
      if (zone.label) zone.label.setVisible(false);
    }

    // Reset create zone
    this.createZone.zone.deactivateGlow();
    this.createZone.zone.hide();
    if (this.createZone.label) this.createZone.label.setVisible(false);
    this.createZoneVisible = false;

    // Clear hover state and slot assignments for new round
    this.currentHover = null;
    this.zoneSlots.clear();
  }

  /**
   * Clear and return ALL bills from ALL zones (active zones + create zone).
   * Use this during phase transitions to ensure no bills are left in scene.
   * Caller is responsible for removing bills from scene and disposing.
   */
  clearAllZoneBills(): Bill[] {
    const allBills: Bill[] = [];

    // Clear bills from all active zones
    for (const zone of this.zones.values()) {
      const bills = zone.zone.clearBills();
      allBills.push(...bills);
    }

    // Clear bills from create zone
    const createBills = this.createZone.zone.clearBills();
    allBills.push(...createBills);

    return allBills;
  }

  showAll(): void {
    for (const zoneId of this.zones.keys()) {
      this.showZone(zoneId);
    }
    if (this.createZoneVisible) {
      this.setCreateZoneVisible(true);
    }
  }

  showZone(zoneId: string): void {
    const zone = this.zones.get(zoneId);
    if (!zone) return;
    if (zone.data?.status === 'locked') return;
    zone.zone.show();
    if (zone.label) zone.label.setVisible(true);
  }

  dispose(): void {
    // Dispose active zones (this will pool them, but we'll dispose pool next)
    for (const zone of this.zones.values()) {
      zone.zone.dispose();
      if (zone.label) zone.label.dispose();
    }
    this.zones.clear();
    this.zoneSlots.clear();

    // Dispose pooled zones
    for (const pooled of this.zonePool) {
      pooled.zone.dispose();
      pooled.label.dispose();
    }
    this.zonePool = [];

    // Dispose create zone
    if (this.createZone.label) {
      this.createZone.label.dispose();
      this.createZone.label = null;
    }
    this.createZone.zone.dispose();
  }

  private upsertZone(zone: BetZone, playerNames: Map<string, string>, position: THREE.Vector3, color: number): void {
    const existing = this.zones.get(zone.id);
    // Show remaining amount left to fade (counts down to 0 → lock)
    const displayAmount = zone.maxFadeAmount - zone.fadedAmount;
    // Sidebet display buffer: server caps how many sidebets are visible at
    // once and queues the rest with `visible: false`. Treat queued zones
    // exactly like locked ones — hide the mesh + label so they don't add
    // to bounds and don't render. Server flips them back to visible when
    // a slot frees up (a visible zone locks).
    if (zone.visible === false) {
      const existingHidden = this.zones.get(zone.id);
      if (existingHidden) {
        existingHidden.data = zone;
        existingHidden.zone.hide();
        if (existingHidden.label) existingHidden.label.setVisible(false);
      }
      // If the zone was never created on this client (just queued from
      // the start), don't bother instantiating visuals — wait until it
      // surfaces.
      return;
    }

    // Banker zone label is hidden until the banker actually places the pot
    // (ownerAmount > 0). Reason: during BANKER_SET_POT the empty zone +
    // 3D "BANK" label fight for the same eye-line as the new instruction
    // banner ("You are the bank — place your bet" / "Wait for the
    // banker…"). Hiding the label until the bet drops keeps a single source
    // of truth on screen at a time. Non-banker zones (sidebets) always
    // have ownerAmount > 0 by construction so this rule is a no-op for them.
    //
    // Locked zones (fully faded, awaiting server removal) always have their
    // label hidden — without this, sidebet zones that finished fading would
    // pop their owner-label back on the next upsertZone tick because their
    // ownerAmount is still > 0 in the snapshot.
    const labelVisible = zone.status !== 'locked'
      && (zone.type !== 'banker' || zone.ownerAmount > 0);

    // Banker / shooter pending: zone exists but owner hasn't placed yet
    // (ownerAmount === 0). Show a lock icon on the zone for spectators —
    // tells them "waiting for the banker / shooter to place their bet".
    // The owner themselves don't see the lock (they're the one acting).
    // Hides as soon as ownerAmount > 0 OR the zone locks. Same rule for
    // cee-lo BANKER_SET_POT and craps SHOOTER_SET_STACK (both are
    // type='banker' on the wire).
    const showOwnerPending = zone.type === 'banker'
      && zone.ownerAmount === 0
      && zone.status !== 'locked'
      && zone.ownerPlayerId !== this.localPlayerId;

    if (existing) {
      existing.data = zone;
      existing.zone.setPosition({ x: position.x, y: position.y, z: position.z });

      // Banker zone disappears when fully matched (locked)
      if (zone.type === 'banker' && zone.status === 'locked') {
        existing.zone.hide();
        if (existing.label) existing.label.setVisible(false);
        existing.lockOverlay?.setVisible(false);
        return;
      }

      if (existing.label) {
        existing.label.update({
          name: playerNames.get(zone.ownerPlayerId) ?? 'Player',
          betType: (zone.type === 'banker' ? 'BANK' : (zone.betType ?? 'SIDE')).toUpperCase(),
          avatarUrl: this.playerAvatars.get(zone.ownerPlayerId),
          betAmount: displayAmount,
          locked: zone.status === 'locked',
        });
      } else {
        existing.label = new DropZoneOwnerLabel(existing.zone.getGroup(), {
          name: playerNames.get(zone.ownerPlayerId) ?? 'Player',
          betType: (zone.type === 'banker' ? 'BANK' : (zone.betType ?? 'SIDE')).toUpperCase(),
          avatarUrl: this.playerAvatars.get(zone.ownerPlayerId),
          betAmount: displayAmount,
          locked: zone.status === 'locked',
        });
      }

      existing.label.setPosition(0, 0.9, -LABEL_Z_OFFSET);
      existing.label.setVisible(labelVisible);

      // Owner-pending lock overlay (banker / shooter zone before placement).
      // Lazily instantiated on first need so we don't spend the load
      // for sidebet zones that never need it.
      if (showOwnerPending && !existing.lockOverlay) {
        existing.lockOverlay = new CreateZoneLockOverlay(existing.zone.getGroup());
      }
      existing.lockOverlay?.setVisible(showOwnerPending);

      return;
    }

    const labelText = playerNames.get(zone.ownerPlayerId) ?? 'Player';
    const betLabel = zone.type === 'banker' ? 'BANK' : (zone.betType ?? 'SIDE');
    const ownerAvatarUrl = this.playerAvatars.get(zone.ownerPlayerId);

    // Try to reuse from pool
    const pooled = this.zonePool.pop();

    let zoneMesh: BettingZone;
    let label: DropZoneOwnerLabel;
    const fadedPlayersPanel = null;

    if (pooled) {
      // Reuse pooled zone
      zoneMesh = pooled.zone;
      zoneMesh.setPosition({ x: position.x, y: position.y, z: position.z });
      zoneMesh.setColors(color, color);
      zoneMesh.attachTo(this.scene);
      zoneMesh.show();

      // Reuse pooled label (now includes bet amount)
      label = pooled.label;
      label.attachTo(zoneMesh.getGroup());
      label.update({ name: labelText, betType: betLabel.toUpperCase(), avatarUrl: ownerAvatarUrl, betAmount: displayAmount, locked: zone.status === 'locked' }, true);
      label.setPosition(0, 0.7, -LABEL_Z_OFFSET);
      label.setVisible(labelVisible);

    } else {
      // Create new zone
      zoneMesh = new BettingZone({
        scene: this.scene,
        position: { x: position.x, y: position.y, z: position.z },
        radius: DEFAULT_RADIUS,
        color,
        glowColor: color,
      });

      label = new DropZoneOwnerLabel(zoneMesh.getGroup(), {
        name: labelText,
        betType: betLabel.toUpperCase(),
        avatarUrl: ownerAvatarUrl,
        betAmount: displayAmount,
        locked: zone.status === 'locked',
      });
      label.setPosition(0, 0.7, -LABEL_Z_OFFSET);
      label.setVisible(labelVisible);

    }

    const newZone: BetZoneVisual = {
      id: zone.id,
      kind: zone.type === 'banker' ? 'banker' : 'sidebet',
      zone: zoneMesh,
      label,
      fadedPlayersPanel,
      data: zone,
      lockOverlay: showOwnerPending ? new CreateZoneLockOverlay(zoneMesh.getGroup()) : null,
    };
    newZone.lockOverlay?.setVisible(showOwnerPending);

    this.zones.set(zone.id, newZone);
  }

  private removeZone(zoneId: string): void {
    const zone = this.zones.get(zoneId);
    if (!zone) return;
    if (this.currentHover === zoneId) {
      this.currentHover = null;
    }

    // Clear bills from zone BEFORE resetting (remove from scene and dispose)
    const bills = zone.zone.clearBills();
    for (const bill of bills) {
      this.scene.remove(bill.getMesh());
      bill.dispose();
    }

    // Reset and pool instead of disposing
    zone.zone.reset();

    if (zone.label) {
      zone.label.reset();
    }

    // The lock overlay is parented to the zone group; reset() detaches
    // the group, but we dispose the overlay explicitly so the next
    // pooled zone doesn't carry a stale reference. Cheap to re-create
    // on the next banker zone that needs one.
    if (zone.lockOverlay) {
      zone.lockOverlay.dispose();
    }

    // Add to pool for reuse (label is guaranteed to exist for dynamic zones)
    this.zonePool.push({
      zone: zone.zone,
      label: zone.label!,
      fadedPlayersPanel: null,
    });

    this.zones.delete(zoneId);
  }

  setHoverTarget(target: { kind: ZoneKind; zoneId?: string } | null): void {
    const next = target ? (target.kind === 'create' ? 'create' : (target.zoneId ?? null)) : null;
    if (next === this.currentHover) return;

    if (this.currentHover) {
      if (this.currentHover === 'create') {
        this.createZone.zone.deactivateGlow();
      } else {
        this.zones.get(this.currentHover)?.zone.deactivateGlow();
      }
    }

    this.currentHover = next;

    if (this.currentHover) {
      if (this.currentHover === 'create') {
        this.createZone.zone.activateGlow();
      } else {
        this.zones.get(this.currentHover)?.zone.activateGlow();
      }
    }
  }

  clearHover(): void {
    if (!this.currentHover) return;
    if (this.currentHover === 'create') {
      this.createZone.zone.deactivateGlow();
    } else {
      this.zones.get(this.currentHover)?.zone.deactivateGlow();
    }
    this.currentHover = null;
  }


  private getHexPositions(count: number, spacing: number, origin: THREE.Vector3): THREE.Vector3[] {
    const results: THREE.Vector3[] = [];
    if (count <= 0) return results;

    const directions = [
      { q: 1, r: 0 },
      { q: 1, r: -1 },
      { q: 0, r: -1 },
      { q: -1, r: 0 },
      { q: -1, r: 1 },
      { q: 0, r: 1 },
    ];

    let ring = 1;
    while (results.length < count) {
      let q = -ring;
      let r = ring;

      for (const dir of directions) {
        for (let step = 0; step < ring; step += 1) {
          const x = spacing * (q + r / 2);
          const z = spacing * (r * Math.sqrt(3) / 2);

          // Skip southern positions (positive z) - reserved for player's create zone
          if (z <= 0.1) {
            results.push(new THREE.Vector3(origin.x + x, origin.y, origin.z + z));
            if (results.length >= count) {
              return results;
            }
          }

          q += dir.q;
          r += dir.r;
        }
      }

      ring += 1;
    }

    return results;
  }
}
